import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import {
	Icon,
	IconArrowDown,
	IconArrowUp,
	IconCity,
	IconHome,
	IconPhone,
	IconSecurity,
	IconStopwatch,
	IconSwitch,
	IconWarningStrong,
} from '@flixbus/honeycomb-icons-react';
import { Button, Divider, Tag } from '@flixbus/honeycomb-react';

import { ProgressBar } from 'common/ProgressBar';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';
import { formatDateTime, getNowInUtc, minutesDuration } from 'utils/datetime';

import { CREW_VALIDATION_RULES } from './const';
import {
	buildCapacityLabel,
	buildJobLabel,
	buildRunLabel,
	deriveClassificationTypeLabel,
	deriveEmploymentTypeLabel,
	derivePresenceLabel,
	getCombinedLabel,
	hasActiveHoldDownAt,
} from './deriveLabels';
import { driverCardText } from './text';

import styles from './styles.module.css';

const DriverCard = observer(
	({
		driver,
		onAssign,
		onExpand,
		isInAssignState,
		isAssignDisabled,
		elegibilityIssues,
		bottomSlot = null,
		bottomSlotVariant = null,
		forceBottomVisible = false,
	}) => {
		const { selectedTimezone, getDepartureStationCity } = rideDetailsStore;
		const [isExpanded, setIsExpanded] = useState(false);

		const {
			crewMember: {
				fullName,
				phone,
				currentEmploymentType,
				currentRun,
				currentJobs,
				//role,
				currentHomeBase,
				driverCard,
				id,
				holdDowns,
			},
			availabilityInfo: {
				idleTimeMinutes,
				nextAvailability,
				remainingCapacityMinutes,
				timeToFreshMinutes,
				maxCapacityMinutes,
				maxWorkingCapacityMinutes,
				remainingWorkingCapacityMinutes,
				isDestinationHomeBase,
				isProtectionInDepartureLocation,
			},
		} = driver;

		const now = getNowInUtc();
		const hasActiveHoldDown = hasActiveHoldDownAt(holdDowns, now);

		const employmentTypeLabel = deriveEmploymentTypeLabel(currentEmploymentType);
		const classificationTypeLabel = deriveClassificationTypeLabel(currentRun);

		const runLabel = buildRunLabel(currentRun);
		const jobLabel = buildJobLabel(currentJobs);

		const presenceLabel = derivePresenceLabel(nextAvailability, now, driverCardText);

		const drivingCapacityLabel = buildCapacityLabel(remainingCapacityMinutes, maxCapacityMinutes);
		const workingCapacityLabel = buildCapacityLabel(
			remainingWorkingCapacityMinutes,
			maxWorkingCapacityMinutes
		);

		const domicileLabel = currentHomeBase !== null ? currentHomeBase.city.name : 'N/A';
		const departureCityId = getDepartureStationCity();
		const isDepartureHomebase = departureCityId === currentHomeBase?.city?.id;

		const issuesArray = elegibilityIssues?.reasons ? elegibilityIssues.reasons : [];

		const handleExpandToggle = () => {
			if (!isExpanded) onExpand?.();

			setIsExpanded((prev) => !prev);
		};

		return (
			<div
				className={classNames(styles.driverCard, {
					[styles.driverCardExpanded]: isExpanded,
					[styles.driverCardWithExtraAction]: bottomSlotVariant === 'action',
					[styles.driverCardWithSelectedReturnJob]: bottomSlotVariant === 'selected',
					[styles.driverCardBottomForced]: forceBottomVisible || bottomSlotVariant === 'selected',
				})}
			>
				<Button
					onClick={handleExpandToggle}
					extraClasses={styles.expandBtn}
					appearance="link"
					size="md"
					display="square"
				>
					<Icon InlineIcon={isExpanded ? IconArrowUp : IconArrowDown} />
				</Button>

				{/* ================= TOP (always visible) ================= */}
				<div className={styles.topSection}>
					<div className={styles.capitalize}>{fullName.toLowerCase()}</div>

					<div className={styles.basicsRow}>
						<span className={`${styles.secondary} ${styles.small}`}>{runLabel}</span>
						<div className={styles.separator} />
						<span className={`${styles.secondary} ${styles.small}`}>{jobLabel}</span>
						<div className={styles.separator} />

						<Tag display="outlined" small>
							<span className={`${styles.small} ${styles.semibold}`}>
								{getCombinedLabel(employmentTypeLabel, classificationTypeLabel)}
							</span>
						</Tag>

						<div className={styles.separator} />

						<Tag display="outlined" small>
							<Icon InlineIcon={IconStopwatch} />
							{minutesDuration(idleTimeMinutes)}
						</Tag>
					</div>

					<div className={styles.basicsRow}>
						<div className={styles.phoneNum}>
							<Icon extraClasses={styles.secondary} InlineIcon={IconPhone} size={3} />
							<span className={`${styles.secondary} ${styles.small} ${styles.semiGap}`}>
								{phone}
							</span>
						</div>
						<div className={styles.separator} />
						<span className={styles.secondary}>{`ID: ${driverCard}`}</span>
					</div>

					<div className={styles.basicsRow}>
						{isProtectionInDepartureLocation && (
							<div className={`${styles.awarnessChip} ${styles.protection}`}>
								<Icon className={styles.icon} InlineIcon={IconSecurity} />
								<span className={styles.semibold}>{driverCardText.onProtection}</span>
							</div>
						)}

						{isDestinationHomeBase && (
							<div className={`${styles.awarnessChip} ${styles.homebase}`}>
								<Icon extraClasses={styles.icon} InlineIcon={IconHome} />
								<span className={styles.semibold}>{driverCardText.arrivesToHomeBase}</span>
							</div>
						)}

						{isDepartureHomebase && (
							<div className={`${styles.awarnessChip} ${styles.homebase}`}>
								<Icon extraClasses={styles.icon} InlineIcon={IconHome} />
								<span className={styles.semibold}>{driverCardText.departsHomeBase}</span>
							</div>
						)}

						{hasActiveHoldDown && (
							<div className={`${styles.awarnessChip} ${styles.holddown}`}>
								<Icon extraClasses={styles.icon} InlineIcon={IconSwitch} />
								<span className={styles.semibold}>{driverCardText.doingHolddown}</span>
							</div>
						)}
					</div>

					<div className={`${styles.basicsRow} ${styles.halfGap}`}>
						<Icon extraClasses={styles.secondary} InlineIcon={IconCity} size={3} />
						<span className={`${styles.small} ${styles.semibold}`}>{driverCardText.homeBase}</span>
						<span className={`${styles.small} ${styles.secondary}`}>{domicileLabel}</span>
					</div>
				</div>

				{/* ================= MIDDLE (toggle by button) ================= */}
				<div className={styles.middleSection}>
					<div className={styles.middleInner}>
						<Divider />

						<div className={styles.expandableRow}>
							<span className={styles.secondary}>{presenceLabel}</span>
							<span>
								{nextAvailability === null
									? 'N/A'
									: formatDateTime(nextAvailability, { timezone: selectedTimezone?.value })}
							</span>
						</div>

						<div className={styles.expandableRow}>
							<span className={styles.secondary}>{driverCardText.preAssignmentIdleTime}</span>
							<span>{minutesDuration(idleTimeMinutes)}</span>
						</div>

						<div className={styles.expandableRow}>
							<span className={styles.secondary}>{driverCardText.timeToFresh}</span>

							{(timeToFreshMinutes === 0 || timeToFreshMinutes === null) && (
								<div className={`${styles.semibold} ${styles.freshness} ${styles.notNeeded}`}>
									{driverCardText.restNotNeeded}
								</div>
							)}

							{timeToFreshMinutes !== null && timeToFreshMinutes !== 0 && (
								<span className={styles.fineprint}>{minutesDuration(timeToFreshMinutes)}</span>
							)}
						</div>

						<Divider />

						<div className={styles.expandableRow}>
							<span className={styles.accent}>{driverCardText.estDrivingCapacity}</span>
							<span className={styles.fineprint}>{drivingCapacityLabel}</span>
						</div>
						{/* Placeholder for the "driver has unreconciled assignment" ribbon.
							The previous stub component lived in ./Unreconciled — it was rendered
							behind a `false && ...` gate and was never seen in production. It has
							been removed to unblock the RideDetails coverage run. Reintroduce here
							when the feature is picked up again. */}

						<div className={styles.expandableRow}>
							<ProgressBar currentValue={remainingCapacityMinutes} maxValue={maxCapacityMinutes} />
						</div>

						<div className={styles.expandableRow}>
							<span className={styles.accent}>{driverCardText.workingCapacity}</span>
							<span className={styles.fineprint}>{workingCapacityLabel}</span>
						</div>

						<div className={styles.expandableRow}>
							<ProgressBar
								currentValue={remainingWorkingCapacityMinutes}
								maxValue={maxWorkingCapacityMinutes}
							/>
						</div>
						<Divider />
						<div className={styles.expandableRow}>
							{issuesArray.length !== 0 && (
								<div className={styles.warningBlock}>
									<div className={styles.issuesHead}>
										<Icon extraClasses={styles.iconRed} InlineIcon={IconWarningStrong} />
										<span className={styles.accent}> {driverCardText.eligibilityIssues}</span>
									</div>
									<div className={styles.errors}>
										{issuesArray.map((issue, index) => {
											const rule = CREW_VALIDATION_RULES[issue.failedRule];
											return (
												<div
													title={rule?.tooltip ?? issue.defaultMessage}
													key={`${issue.failedRule}-${index}`}
													className={styles.error}
												>
													<span className={styles.dot} />
													<span>{rule?.shorthand ?? issue.defaultMessage}</span>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ================= BOTTOM (hover reveal, logic unchanged) ================= */}
				<div className={`${styles.bottomSection}`}>
					<div className={styles.bottomInner}>
						{bottomSlot}
						<div className={`${styles.basicsRow} ${styles.even} ${styles.marginTop}`}>
							<Button
								loading={isInAssignState}
								disabled={isAssignDisabled}
								onClick={onAssign}
								appearance="primary"
								extraClasses={styles.btn}
							>
								{driverCardText.assign}
							</Button>

							<Button
								Elem="a"
								target="_blank"
								href={`/crew-management/${id}`}
								extraClasses={styles.btn}
							>
								{driverCardText.viewProfile}
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}
);

export default DriverCard;
